import React, { useEffect, useMemo, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { FormikErrors } from 'formik';
import { LayoutSidebar, PayeeReviewItem, PaymentReviewSidebar, SectionHeader } from 'components';
import { ISidebarNotification } from 'components/Sidebar/components/Sidebar';
import { HTTPStatusCode, MakePaymentNotification } from 'config/const';
import { HoldbackModal } from 'modals';
import { clearRequest, selectAPIRequestErrors } from 'store/ApiClient';
import { selectCurrentEmployeeRole, selectCurrentEmployeeType } from 'store/Employers';
import { Payment } from 'store/Payment';
import { IStore } from 'store/types';
import { IApiErrorPayloadWithCode, IEmployerSite, IPayee, ISiteRolePolicy, ISubmitPaymentPayload, PermissionType } from 'types';
import { getIncludedPayoutTypes, getRolePolicy, hasSitePermission } from 'utils';
import { MakePaymentHeader } from '../../components';
import { MPContent } from '../../styles';
import { MakePaymentProps } from '../../types';

interface IReviewPaymentProps extends MakePaymentProps {
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  createPaymentsEnabled: boolean;
  cashEnabled: boolean;
  payees: IPayee[];
  currentSite?: IEmployerSite;
  rolePolicy: ISiteRolePolicy;
  siteUsesHoldbacks?: boolean;
  showOverdraftWarning: boolean;
  overdraftText: React.ReactNode;
  overdraftContinue: boolean;
  errors: FormikErrors<ISubmitPaymentPayload>;
}

dayjs.extend(customParseFormat);

const PaymentReview = ({
  setFieldValue,
  createPaymentsEnabled,
  onBack,
  onNext,
  currentSite,
  businessDate,
  pageHeaderContent,
  cashEnabled,
  payees,
  totals,
  showOverdraftWarning,
  overdraftText,
  overdraftContinue,
  errors,
  rolePolicy,
  siteUsesHoldbacks = false,
}: IReviewPaymentProps) => {
  const dispatch = useDispatch();
  const currentEmployeeType = useSelector(selectCurrentEmployeeType);
  const currentEmployeeRole = useSelector(selectCurrentEmployeeRole);
  const apiSubmitErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, Payment.CREATE_PAYMENT_REQUEST)) as IApiErrorPayloadWithCode;
  const [selectedPayeeIndex, setSelectedPayeeIndex] = useState<number>();
  const currentEmployeePolicy = getRolePolicy(rolePolicy, currentEmployeeType, currentEmployeeRole);

  useEffect(() => () => dispatch(clearRequest(Payment.CREATE_PAYMENT_REQUEST)), []);

  const includedPayouts = useMemo(() => getIncludedPayoutTypes(payees, siteUsesHoldbacks), [payees, siteUsesHoldbacks]);

  const [showHoldbackModal, hideHoldbackModal] = useModal(() => {
    const payee = payees[selectedPayeeIndex];
    const onHoldbackChange = (holdback: number) => setFieldValue(`payees[${selectedPayeeIndex}].holdbackAmount`, holdback);
    return <HoldbackModal defaultValue={payee.holdbackAmount} hideModal={hideHoldbackModal} onChange={onHoldbackChange} payee={payee} disabled={!currentEmployeePolicy?.holdbackAdjustment} />;
  }, [payees, selectedPayeeIndex, currentEmployeePolicy, setFieldValue]);

  const hasSubmitPaymentPermissions = hasSitePermission(currentSite?.permissions, PermissionType.PAYMENT_SUBMIT);
  const continueButtonDisabled = !createPaymentsEnabled || !overdraftContinue || !hasSubmitPaymentPermissions || errors?.payees?.length > 0;

  const buildSidebarNotifications = (): ISidebarNotification[] => {
    const notifications: ISidebarNotification[] = [];

    // Payments Disabled
    if (!createPaymentsEnabled) {
      notifications.push(MakePaymentNotification.paymentsDisabled);
    }

    // Payees Error
    if (typeof errors?.payees === 'string') {
      notifications.push({ ...MakePaymentNotification.payeeError, message: errors?.payees });
    }

    // Overdraft Warning
    if (showOverdraftWarning) {
      notifications.push({ ...MakePaymentNotification.overdraftWarning, message: overdraftText, preventSubmit: !overdraftContinue });
    }

    // Duplicate Payment
    if (apiSubmitErrors?.statusCode === HTTPStatusCode.CONFLICT && apiSubmitErrors.errorMessages[0].startsWith('payment_date')) {
      notifications.push(MakePaymentNotification.duplicatePayment);
    } else {
      // System Errors
      apiSubmitErrors?.errorMessages?.forEach((error) => {
        notifications.push({ ...MakePaymentNotification.systemError, message: error, preventSubmit: false });
      });
    }

    // Payment Permissions
    if (!hasSubmitPaymentPermissions) {
      notifications.push(MakePaymentNotification.permissionDenied);
    }

    return notifications;
  };

  return (
    <LayoutSidebar
      data-cy="payment-review"
      content={
        <>
          <MakePaymentHeader pageHeaderContent={pageHeaderContent} />

          <MPContent>
            <SectionHeader title="Review Payments" value={`(${payees.length})`} />
            {payees.map((payee: IPayee, index: number) => (
              <PayeeReviewItem
                key={`payee_${payee.id}`}
                payee={payee}
                onEditHoldback={() => {
                  setSelectedPayeeIndex(index);
                  showHoldbackModal();
                }}
                onEditNote={(note) => setFieldValue(`payees[${index}].note`, note)}
                includedPayouts={includedPayouts}
              />
            ))}
          </MPContent>
        </>
      }
      sidebar={
        <PaymentReviewSidebar
          onBack={onBack}
          onContinue={onNext}
          businessDate={dayjs(businessDate, 'MM-DD-YYYY')}
          continueDisabled={continueButtonDisabled}
          showCash={cashEnabled}
          showHoldbacks={siteUsesHoldbacks}
          totals={totals}
          notifications={buildSidebarNotifications()}
        />
      }
    />
  );
};

export default PaymentReview;
